import { useWidget } from '@telescope/cassini-hooks';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  ButtonGroup,
  CloseButton,
  Container,
  Flex,
  useTheme,
  useToast,
} from '@telescope/cassini-ui';
import { Open } from 'generated-types/widget';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'utils';
import { useAuth } from '../lib';
import { AuthMethod } from '../utils';

export const AuthNav = () => {
  const { user, logout, hasRegistration, isMethodEnabled } = useAuth();
  const { data } = useWidget({ select: (data: Open) => data.widget.globalViews.auth });
  const { content, settings } = data!;
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colorScheme = theme.settings.defaultColorScheme;

  // Don't show auth nav if login is disabled
  if (isMethodEnabled(AuthMethod.ANONYMOUS)) {
    return null;
  }

  const handleLogout = async () => {
    await logout();

    if (settings.isLogoutNotificationEnabled) {
      toast({
        title: content.logoutNotification.title,
        description: content.logoutNotification.description,
        duration: 3000,
        isClosable: true,
        position: 'top',
        render: ({ id, onClose }) => (
          <Alert
            alignItems="start"
            borderRadius="md"
            boxShadow="lg"
            paddingEnd={8}
            textAlign="start"
            width="auto"
            variant={'solid'}
            colorScheme={colorScheme}>
            <AlertIcon />
            <Box flex="1" maxWidth="100%">
              <AlertTitle>{content.logoutNotification.title}</AlertTitle>
              <AlertDescription>{content.logoutNotification.description}</AlertDescription>
            </Box>
            <CloseButton size="sm" onClick={onClose} position="absolute" insetEnd={1} top={1} />
          </Alert>
        ),
      });
    }
  };

  return (
    <Flex className="AuthNav" as={Container} justifyContent="flex-end" py={5}>
      <ButtonGroup className="AuthNav__auth-btn-group" size="sm">
        {user ? (
          <Button className="AuthNav__logout-btn" variant="solid" px={10} onClick={handleLogout}>
            {content.user.logoutCTA}
          </Button>
        ) : (
          <>
            <Button
              className="AuthNav__login-btn"
              px={10}
              onClick={() => navigate(ROUTES.LOGIN, { state: { backgroundLocation: location }, replace: true })}>
              {content.user.loginCTA}
            </Button>
            {hasRegistration && (
              <Button
                className="AuthNav__register-btn"
                variant="solidInverse"
                px={10}
                onClick={() => navigate(ROUTES.REGISTER, { state: { backgroundLocation: location }, replace: true })}>
                {content.user.signUpCTA}
              </Button>
            )}
          </>
        )}
      </ButtonGroup>
    </Flex>
  );
};
